import axios from "axios";

import store from "../redux/store";
import { extractErrorMessage, requestDateFormat } from "./utils";
import { ACCESS_TOKEN_KEY, MAIN_SYSTEMS, REFRESH_TOKEN_KEY } from "../constants";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json;charset=utf-8",
  },
});

const requestSuccessInterceptor = async (config) => {
  const authToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (authToken && authToken !== "null") {
    config.headers.Authorization = "Bearer " + authToken;
  }

  let cnfg = {};
  const state = store.getState();
  const clusterId = state.selectCSC?.cluster?.id;
  const segments = state.selectCSC?.segments;
  const selectedCountries = state.selectCSC?.selectedCountries;

  if (clusterId && MAIN_SYSTEMS.indexOf(state.selectSystemType?.systemType) !== -1) {
    cnfg["clusters"] = clusterId;
  }

  if (segments?.length) {
    cnfg["segments"] = segments.map((segment) => segment).join(",");
  }

  if (selectedCountries?.length) {
    cnfg["countries"] = selectedCountries.map((country) => country.id).join(",");
  }

  config.params = {
    ...config.params,
    ...cnfg,
  };

  return config;
};

api.interceptors.request.use(requestSuccessInterceptor);

api.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    const originalConfig = err.config;

    if (
      err?.response?.status === 401 &&
      !originalConfig._retry &&
      originalConfig.url !== "/auth/refresh-token/" &&
      originalConfig.url !== "/auth/login/"
    ) {
      originalConfig._retry = true;

      try {
        const rs = await api.post("/auth/refresh-token/", {
          refresh: localStorage.getItem(REFRESH_TOKEN_KEY),
        });

        const { access } = rs.data;
        localStorage.setItem(ACCESS_TOKEN_KEY, access);
        return api(originalConfig);
      } catch (_error) {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.location.href = "/login";
        return Promise.reject(_error);
      }
    }

    return Promise.reject(err);
  }
);

export const cleanData = (data) => {
  if (!data) return data;
  for (const k in data) {
    if (typeof data[k] === "object" && !Array.isArray(data[k]) && data[k] !== null) {
      cleanData(data[k]);
    }
    if (Array.isArray(data[k])) {
      data[k].forEach((item) => cleanData(item));
    }
    if (data[k] === "") {
      data[k] = null;
    }
  }
  return data;
};

export const getData = (url, callback, queryParams = null, configs = {}) => {
  if (queryParams) {
    const { period, startDate, endDate } = queryParams;
    if (period?.name === "RANGE" && endDate) {
      queryParams["from_date"] = requestDateFormat(startDate);
      queryParams["to_date"] = requestDateFormat(endDate);
    } else if (["YTD", "Acc.", "20Y"].includes(period?.name)) {
      queryParams["date_filter"] = period.filter;
    }
    delete queryParams.period;
    delete queryParams.startDate;
    delete queryParams.endDate;
  }
  return api
    .get(url, { params: queryParams, ...configs })
    .then((res) => res.data)
    .then((data) => callback(data));
};

export const postData = (url, callBack, data = null) => {
  return api
    .post(url, cleanData(data))
    .then((res) => res.data)
    .then((d) => callBack(d));
};

export const putData = (url, callBack, data = null, headers = {}) => {
  return api
    .put(url, cleanData(data), { headers })
    .then((res) => res.data)
    .then((d) => callBack(d));
};

export const getErrorMessage = (e) => {
  let message = "Something went wrong.";
  if (e?.response?.status !== 500 && e?.response?.data) {
    message = extractErrorMessage(e?.response?.data);
  }
  return message;
};

export const MEDIA_SERVICE_FILES_BASE_URL = process.env.REACT_APP_API_URL.replace(
  "/api",
  "/media/service_files"
);

export { api };
